import { create } from 'zustand'
import { authService, type User } from './services/auth'

const readStoredUser = (): User | null => {
  const rawUser = localStorage.getItem('user')
  if (!rawUser) {
    return null
  }

  try {
    const parsedUser = JSON.parse(rawUser)
    return {
      ...parsedUser,
      isPlatformAdmin: Boolean(parsedUser?.isPlatformAdmin),
    }
  } catch {
    return null
  }
}

const persistSession = (user: User, token: string) => {
  localStorage.setItem('authToken', token)
  localStorage.setItem('user', JSON.stringify(user))
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    companyName: string,
    signup?: { signupToken?: string; discountCode?: string },
  ) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setSession: (user: User, token: string) => void
  clearError: () => void
}

const formatApiError = (error: any, fallback: string): string => {
  const details = error.response?.data?.error?.details

  if (Array.isArray(details) && details.length > 0) {
    const messages = details
      .map((detail: any) => {
        if (typeof detail === 'string') {
          return detail
        }
        if (typeof detail?.message === 'string') {
          return detail.message
        }
        return null
      })
      .filter((detail: string | null): detail is string => detail !== null)

    if (messages.length > 0) {
      return messages.join('\n')
    }
  }

  return error.response?.data?.error?.message || fallback
}

export const useAuthStore = create<AuthState>((set) => ({
  user: readStoredUser(),
  token: localStorage.getItem('authToken') || null,
  isLoading: false,
  error: null,

  register: async (email, password, firstName, lastName, companyName, signup) => {
    set({ isLoading: true, error: null })
    try {
      const response = await authService.register({
        email,
        password,
        firstName,
        lastName,
        companyName,
        signupToken: signup?.signupToken || undefined,
        discountCode: signup?.discountCode || undefined,
      })

      const { user, token } = response.data.data
      persistSession(user, token.access_token)

      set({
        user,
        token: token.access_token,
        isLoading: false,
      })
    } catch (error: any) {
      const message = formatApiError(error, 'Registration failed')
      set({ isLoading: false, error: message })
      throw error
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null })
    try {
      const response = await authService.login({ email, password })

      const { user, token } = response.data.data
      persistSession(user, token.access_token)

      set({
        user,
        token: token.access_token,
        isLoading: false,
      })
    } catch (error: any) {
      const message = formatApiError(error, 'Login failed')
      set({ isLoading: false, error: message })
      throw error
    }
  },

  logout: () => {
    authService.logout()
    set({ user: null, token: null })
  },

  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user))
    set({ user })
  },

  setSession: (user, token) => {
    persistSession(user, token)
    set({ user, token, isLoading: false, error: null })
  },

  clearError: () => set({ error: null }),
}))

interface CompanyState {
  companies: any[]
  selectedCompany: any | null
  isLoading: boolean
  setCompanies: (companies: any[]) => void
  setSelectedCompany: (company: any) => void
}

export const useCompanyStore = create<CompanyState>((set) => ({
  companies: [],
  selectedCompany: null,
  isLoading: false,
  setCompanies: (companies) => set({ companies }),
  setSelectedCompany: (company) => set({ selectedCompany: company }),
}))
